import { useMutation } from "@apollo/client";
import type {
  UpdateOfferingInput as UpdateOfferingInputType,
  UpdateOfferingPayload,
  OfferingPatch,
} from "@casey/db-types";
import { DELETE_OFFERING, GET_ORGANIZATION_OFFERINGS } from "../gql";

interface DeleteOfferingInput {
  id: string;
  archivedByUserId: string;
}

interface DeleteOfferingVariables {
  input: UpdateOfferingInputType;
}

interface DeleteOfferingResponse {
  updateOffering: UpdateOfferingPayload;
}

export function useDeleteOffering() {
  const [deleteOfferingMutation, { data, loading, error }] = useMutation<
    DeleteOfferingResponse,
    DeleteOfferingVariables
  >(DELETE_OFFERING, {
    // Automatically refetch team offerings after deletion
    refetchQueries: [{ query: GET_ORGANIZATION_OFFERINGS }],
    // Optimistically remove from cache
    update: (cache, { data }, { variables }) => {
      if (data?.updateOffering?.offering) {
        cache.evict({
          id: cache.identify({
            __typename: "Offering",
            id: variables?.input.id,
          }),
        });
      }
    },
  });

  const deleteOffering = async (deleteData: DeleteOfferingInput) => {
    try {
      const offeringPatch: OfferingPatch = {
        archivedAt: new Date().toISOString(),
        archivedByUserId: deleteData.archivedByUserId,
      };

      const result = await deleteOfferingMutation({
        variables: {
          input: {
            id: deleteData.id,
            patch: offeringPatch,
          },
        },
      });

      const archivedOffering = result.data?.updateOffering?.offering;
      return archivedOffering;
    } catch (err) {
      console.error("Error deleting offering:", err);
      throw err;
    }
  };

  return {
    deleteOffering,
    data: data?.updateOffering?.offering,
    loading,
    error,
  };
}

export type { DeleteOfferingInput };
